/**
 * The one error vocabulary of PodeContratar: every API error body and every
 * MCP tool error uses these codes. The message is en-US and descriptive; UI
 * copy is chosen by the client from the code, never parsed from the message.
 */
export const API_ERROR_CODES = {
  invalid_arguments: { status: 400, text: "an argument is missing or malformed; `details.fields` lists each problem." },
  unauthorized: { status: 401, text: "the request carries no valid session." },
  payment_required: { status: 402, text: "the action needs a payment first." },
  forbidden: { status: 403, text: "the session is valid but lacks permission for this action." },
  not_found: { status: 404, text: "the id does not exist or is not visible to this user." },
  already_exists: { status: 409, text: "a record with this identifier already exists; nothing was created." },
  confirmation_mismatch: { status: 409, text: "a destructive action's confirmation did not match the record; nothing was changed." },
  not_allowed: { status: 409, text: "a business rule refuses the change; `details.reason` names the rule." },
  expired: { status: 410, text: "the link or token has expired." },
  payload_too_large: { status: 413, text: "the upload is larger than allowed." },
  not_ready: { status: 409, text: "the resource is not ready yet." },
  email_not_confirmed: { status: 403, text: "the account's email is not confirmed yet." },
  plan_required: { status: 402, text: "the feature needs a paid plan." },
  monitoring_cap_reached: { status: 409, text: "the plan's limit of monitored documents is reached." },
  idempotency_in_progress: { status: 409, text: "a request with the same idempotency key is still running." },
  idempotency_key_reused: { status: 422, text: "the idempotency key was already used for a different request." },
  rate_limited: { status: 429, text: "too many requests; `details.retry_after` is in seconds." },
  upstream_error: { status: 502, text: "Stripe, Zarv ID or another provider failed; nothing was changed." },
  unavailable: { status: 503, text: "the service is temporarily unavailable." },
  internal_error: { status: 500, text: "something failed on the server." },
} as const;

export type ApiErrorCode = keyof typeof API_ERROR_CODES;

export function httpStatusFor(code: ApiErrorCode): number {
  return API_ERROR_CODES[code].status;
}

/**
 * Thrown by handlers; the server turns it into the error envelope.
 *
 * Fields are assigned in the constructor body rather than via TypeScript
 * parameter properties: Node's built-in type-stripping (`node scripts/*.ts`,
 * no build step) only erases type syntax, and a parameter property is a
 * runtime construct it cannot lower.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly details?: Record<string, unknown>;

  constructor(code: ApiErrorCode, message: string, details?: Record<string, unknown>) {
    super(message);
    this.code = code;
    this.details = details;
  }
}
